import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Loader2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const field = "bg-zinc-900/70 border-zinc-800 text-sm";
const EMPTY = {
  name: "",
  website: "",
  industry: "",
  location: "",
  description: "",
  currency: "",
  gstin: "",
  notes: "",
  contacts: [],
};

export default function Clients() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/clients").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const enrich = async () => {
    if (!form.website) return toast.error("Add a website URL first");
    setEnriching(true);
    try {
      const { data } = await api.post("/clients/enrich", { website: form.website });
      setForm((f) => ({
        ...f,
        name: f.name || data.name || "",
        description: data.description || f.description,
        industry: data.industry || f.industry,
        location: data.location || f.location,
        website: data.website || f.website,
      }));
      toast.success("Filled from website");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEnriching(false);
    }
  };

  const submit = async () => {
    try {
      if (editing) await api.put(`/clients/${editing}`, form);
      else await api.post("/clients", form);
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
      toast.success(editing ? "Client updated" : "Client added");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/clients/${id}`);
    setDetail(data);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      load();
      toast.success("Client removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell
      title="Clients"
      subtitle="Companies, contacts and their proposal history"
      actions={
        <Button
          data-testid="add-client-btn"
          variant="outline"
          onClick={() => {
            setForm(EMPTY);
            setEditing(null);
            setOpen(true);
          }}
          className="h-9 border-zinc-800 text-zinc-300"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add client
        </Button>
      }
    >
      {!rows ? (
        <div className="h-40 grid place-items-center text-zinc-600">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : !rows.length ? (
        <div data-testid="clients-empty" className="nv-card p-12 text-center">
          <p className="text-zinc-300 font-medium">No clients yet</p>
          <p className="text-sm text-zinc-500 mt-1">Add one and we'll auto-fill details from their website.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map((c) => (
            <div key={c.id} data-testid={`client-card-${c.id}`} className="nv-card p-4 nv-rise">
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => openDetail(c.id)} className="text-left min-w-0">
                  <p className="font-semibold text-zinc-100 truncate">{c.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{c.industry || "—"} · {c.location || "—"}</p>
                </button>
                <button data-testid={`delete-client-${c.id}`} onClick={() => remove(c.id)} className="p-1.5 rounded hover:bg-red-500/15">
                  <Trash2 className="w-3.5 h-3.5 text-zinc-500" />
                </button>
              </div>
              {c.description && <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{c.description}</p>}
              <div className="mt-3 flex items-center gap-3 text-[11px] font-num text-zinc-500">
                <span>{c.proposal_count} proposals</span>
                {c.currency && <span className="text-indigo-400">{c.currency}</span>}
                {c.contacts?.length > 0 && <span>{c.contacts.length} contacts</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-panel border-zinc-800 max-w-lg max-h-[88vh] overflow-y-auto nv-scroll">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit client" : "New client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input data-testid="client-website-input" className={field} value={form.website} onChange={set("website")} placeholder="company.com" />
              <Button data-testid="client-enrich-btn" variant="outline" onClick={enrich} disabled={enriching} className="border-indigo-500/30 text-indigo-300 shrink-0">
                {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                <span className="ml-1.5 hidden sm:inline">Auto-fill</span>
              </Button>
            </div>
            <Input data-testid="client-name-input" className={field} value={form.name} onChange={set("name")} placeholder="Company name" />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="client-industry-input" className={field} value={form.industry} onChange={set("industry")} placeholder="Industry" />
              <Input data-testid="client-location-input" className={field} value={form.location} onChange={set("location")} placeholder="Location" />
            </div>
            <Textarea data-testid="client-description-input" className={field} value={form.description} onChange={set("description")} placeholder="What they do" />
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="client-currency-select" className={`h-9 rounded-md px-2 w-full ${field}`} value={form.currency} onChange={set("currency")}>
                <option value="">Use account currency</option>
                {["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <Input data-testid="client-gstin-input" className={field} value={form.gstin} onChange={set("gstin")} placeholder="GSTIN (optional)" />
            </div>

            <div className="space-y-2">
              <p className="text-xs text-zinc-500">Contacts</p>
              {form.contacts.map((ct, i) => (
                <div key={i} className="rounded-lg border border-zinc-800 p-2.5 space-y-2">
                  <div className="flex gap-2">
                    <Input data-testid={`contact-name-${i}`} className={field} value={ct.name} placeholder="Name" onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                    <Input className={field} value={ct.role} placeholder="Role" onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)) })} />
                    <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, contacts: form.contacts.filter((_, j) => j !== i) })}>
                      <X className="w-4 h-4 text-zinc-500" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Input className={field} value={ct.email} placeholder="Email" onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)) })} />
                    <Input className={field} value={ct.phone} placeholder="Phone" onChange={(e) => setForm({ ...form, contacts: form.contacts.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)) })} />
                  </div>
                </div>
              ))}
              <Button data-testid="add-contact-btn" variant="outline" size="sm" className="border-zinc-800 text-zinc-300" onClick={() => setForm({ ...form, contacts: [...form.contacts, { name: "", email: "", phone: "", role: "", is_primary: false }] })}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add contact
              </Button>
            </div>

            <Textarea data-testid="client-notes-input" className={field} value={form.notes} onChange={set("notes")} placeholder="Internal notes" />
            <Button data-testid="save-client-btn" onClick={submit} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
              {editing ? "Save changes" : "Add client"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-panel border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>{detail?.name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">{detail.description || "No description yet."}</p>
              <div className="grid grid-cols-2 gap-2 text-xs font-num text-zinc-400">
                <span>Industry: {detail.industry || "—"}</span>
                <span>Location: {detail.location || "—"}</span>
                <span>Currency: {detail.currency || "account default"}</span>
                <span>GSTIN: {detail.gstin || "—"}</span>
              </div>
              {detail.contacts?.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-zinc-500">Contacts</p>
                  {detail.contacts.map((c, i) => (
                    <p key={i} className="text-sm text-zinc-300">
                      {c.name} <span className="text-zinc-500">· {c.role} · {c.email}</span>
                    </p>
                  ))}
                </div>
              )}
              <div>
                <p className="text-xs text-zinc-500 mb-1.5">Proposal history</p>
                {!detail.proposals?.length ? (
                  <p className="text-sm text-zinc-600">No proposals yet.</p>
                ) : (
                  <div className="divide-y divide-zinc-800/60">
                    {detail.proposals.map((p) => (
                      <button key={p.id} onClick={() => navigate(`/editor/${p.id}`)} className="w-full text-left py-2 flex justify-between hover:bg-zinc-800/30 px-2 -mx-2 rounded">
                        <span className="text-sm text-zinc-200 truncate">{p.title}</span>
                        <span className="text-xs font-num text-zinc-500 uppercase">{p.status}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button
                data-testid="edit-client-btn"
                variant="outline"
                className="border-zinc-800 text-zinc-300"
                onClick={() => {
                  setForm({ ...EMPTY, ...detail, contacts: detail.contacts || [] });
                  setEditing(detail.id);
                  setDetail(null);
                  setOpen(true);
                }}
              >
                Edit client
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
